// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../../store/AppContext.js';
import { clearCache } from '../../lib/indexedDb.js';
import { fetchSyncLog, decryptBlobs } from '../../lib/syncClient.js';
import { HusholdMark, MenuIcon } from '../mobile/icons.js';
import { APP_VERSION, BUILD_STAMP } from '../../lib/appVersion.js';

interface TopNavProps {
  worker: Worker | null;
  keyData: ArrayBuffer | null;
  // Opens the off-canvas sidebar drawer on small screens; the hamburger that
  // calls it is hidden at md+ where the sidebar is always visible.
  onMenuClick?: () => void;
}

export function TopNav({ worker, keyData, onMenuClick }: TopNavProps) {
  const { t, i18n } = useTranslation(['common', 'mobile', 'errors']);
  const { state, dispatch } = useApp();
  const { auth, sync, ledger } = state;
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  async function handleSync() {
    if (!auth.token || !keyData || !worker) return;
    dispatch({ type: 'SYNC_START' });
    try {
      const config = { baseUrl: auth.syncUrl, token: auth.token };
      const blobs = await fetchSyncLog(config, ledger.lastSequence);
      const events = await decryptBlobs(blobs, worker, keyData);
      if (events.length > 0) {
        // Persisted as an encrypted snapshot by MainApp's effect (SEC-05).
        dispatch({ type: 'EVENTS_LOADED', events });
      }
      dispatch({ type: 'SYNC_SUCCESS' });
    } catch (err) {
      dispatch({ type: 'SYNC_ERROR', message: err instanceof Error ? err.message : t('errors:syncFailed') });
    }
  }

  async function handleLogout() {
    setUserMenuOpen(false);
    await clearCache();
    dispatch({ type: 'AUTH_LOGOUT' });
  }

  const syncColor = {
    idle: 'text-text-subtle',
    syncing: 'text-accent',
    success: 'text-positive',
    error: 'text-negative',
  }[sync.status];

  return (
    <>
      <header className="h-12 bg-bg-surface border-b border-border px-4 flex items-center justify-between shrink-0 no-print">
        <div className="flex items-center gap-2">
          <button
            onClick={onMenuClick}
            aria-label={t('a11y.openNavigation')}
            className="md:hidden -ml-1 p-1 text-text-muted hover:text-text transition-colors"
          >
            <MenuIcon size={20} />
          </button>
          <Link to="/" className="flex items-center gap-1.5 group">
            <span className="inline-flex items-center gap-1.5 text-base font-semibold text-text group-hover:text-accent transition-colors">
              <HusholdMark size={16} />
              {t('appName')}
            </span>
            <span className="text-text-subtle text-xs" title={BUILD_STAMP}>{APP_VERSION}</span>
          </Link>
        </div>

        <div className="flex items-center gap-4">
          {/* Sync status */}
          <span className={`text-xs ${syncColor}`}>
            {sync.status === 'syncing' ? t('sync.syncing') : sync.status === 'error' ? t('sync.error') : t('sync.synced')}
          </span>

          <button
            onClick={handleSync}
            disabled={sync.status === 'syncing' || !auth.token}
            className="px-3 py-1 text-xs bg-accent text-white hover:bg-accent-hover disabled:opacity-40 transition-colors"
          >
            {sync.status === 'syncing' ? t('sync.syncing') : t('sync.sync')}
          </button>

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 px-2 py-1 text-sm text-text-muted hover:text-text transition-colors"
            >
              <div className="w-5 h-5 bg-accent-muted border border-accent text-accent text-xs flex items-center justify-center font-semibold shrink-0">
                {auth.username?.charAt(0).toLocaleUpperCase(i18n.language)}
              </div>
              <span className="text-sm">{auth.username}</span>
            </button>

            {userMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                <div className="absolute right-0 mt-1 w-52 bg-bg-surface border border-border z-50">
                  <button
                    onClick={() => { setUserMenuOpen(false); navigate('/settings'); }}
                    className="w-full text-left px-4 py-2 text-sm text-text-muted hover:bg-bg-elevated hover:text-text"
                  >
                    {t('nav.settings')}
                  </button>
                  <div className="border-t border-border my-0.5" />
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-negative hover:bg-bg-elevated"
                  >
                    {t('actions.signOut')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>
    </>
  );
}
